/*       */
import Immutable from 'app/utils/immutable/Immutable';
import { sortBy } from 'app/utils/lo/lo';
import {
    OPEN_THREAD_VIEW, OPEN_THREADS_LIST, CLOSE_THREAD_PANEL, SET_THREADS_FILTER, RESET_THREAD_STATE,
    LOAD_THREAD_MESSAGES_STARTED, LOAD_THREAD_MESSAGES, LOAD_THREADS_LIST_STARTED, LOAD_THREADS_LIST,
    ADD_THREAD_MESSAGE, EDIT_THREAD_MESSAGE, TOGGLE_THREAD_FOLLOW, SET_THREADS_SEARCH,
} from 'store/actions/chat/chatThreadActions';
import { DELETE_CHAT_MESSAGE, EDIT_ROOM_MESSAGES, UPDATE_CHAT_MESSAGE } from 'store/actions/chat/chatActions';
import { BUILD as SIDEBAR_BUILD } from 'store/actions/sidebar/sidebarActions';

const initialState = {
    isOpen: false,
    mode: 'list',
    rid: null,
    tmid: null,
    parentMessage: null,
    messages: [],
    threadsList: [],
    filter: 'all',
    searchText: '',
    isLoadingMessages: false,
    isLoadingThreads: false,
};

export default (state = Immutable(initialState), { type, payload, error, meta } = {}) => {
    switch (type) {
        case OPEN_THREAD_VIEW: {
            const { rid, parentMessage } = payload;
            return Immutable({
                ...state, isOpen: true, mode: 'thread', rid,
                tmid: parentMessage.id, parentMessage, messages: [],
            });
        }
        case OPEN_THREADS_LIST:
            return Immutable({ ...state, isOpen: true, mode: 'list', rid: payload.rid, tmid: null, parentMessage: null, messages: [], searchText: '' });
        case CLOSE_THREAD_PANEL:
            return Immutable({ ...state, isOpen: false });
        case SIDEBAR_BUILD:
            // Thread panel is the right-hand sidebar; any global sidebar opening
            // (Members/Attachments/Sharing/About/entity sidebars) is mutually exclusive
            // with it — close the thread atomically in the same dispatch (no flicker).
            return (payload?.isOpen && state?.isOpen) ? Immutable({ ...state, isOpen: false }) : state;
        case RESET_THREAD_STATE:
            return Immutable(initialState);
        case SET_THREADS_FILTER:
            return Immutable({ ...state, filter: payload });
        case SET_THREADS_SEARCH:
            return Immutable({ ...state, searchText: payload || '' });
        case LOAD_THREAD_MESSAGES_STARTED:
            return Immutable({ ...state, isLoadingMessages: true });
        case LOAD_THREAD_MESSAGES: {
            if (error) {
                return Immutable({ ...state, error, meta, isLoadingMessages: false });
            }
            const { tmid, messages } = payload;
            if (tmid !== state.tmid) {
                return Immutable({ ...state, isLoadingMessages: false });
            }
            const known = new Set(state.messages.map(m => m.id));
            const merged = [...state.messages, ...messages.filter(m => !known.has(m.id))];
            return Immutable({ ...state, isLoadingMessages: false, messages: sortBy(merged, 'createDate') });
        }
        case LOAD_THREADS_LIST_STARTED:
            return Immutable({ ...state, isLoadingThreads: true });
        case LOAD_THREADS_LIST: {
            if (error) {
                return Immutable({ ...state, error, meta, isLoadingThreads: false });
            }
            return Immutable({ ...state, isLoadingThreads: false, threadsList: payload.threads });
        }
        case ADD_THREAD_MESSAGE: {
            if (error) {
                return Immutable({ ...state, error, meta });
            }
            const { message } = payload;
            const inOpenThread = state.isOpen && message.tmid === state.tmid;
            const messages = inOpenThread && !state.messages.some(m => m.id === message.id)
                ? sortBy([...state.messages, message], 'createDate')
                : state.messages;
            return Immutable({ ...state, messages });
        }
        case TOGGLE_THREAD_FOLLOW: {
            // following lives in the thread's replies[] (RC followers) — add/remove my id,
            // in both the list card and the open thread's pinned parent.
            const { tmid, userId, follow } = payload || {};
            if (!tmid || !userId) {
                return state;
            }
            const patchReplies = (replies = []) =>
                (follow ? (replies.includes(userId) ? replies : [...replies, userId]) : replies.filter(id => id !== userId));
            const threadsList = state.threadsList.map(t => (t.id === tmid ? { ...t, replies: patchReplies(t.replies) } : t));
            const parentMessage = state.parentMessage && state.parentMessage.id === tmid
                ? { ...state.parentMessage, replies: patchReplies(state.parentMessage.replies) }
                : state.parentMessage;
            return Immutable({ ...state, threadsList, parentMessage });
        }
        case EDIT_THREAD_MESSAGE: {
            const { message } = payload;
            const idx = state.messages.findIndex(m => m.id === message.id);
            if (idx < 0) {
                return state;
            }
            const messages = [...state.messages];
            messages[idx] = message;
            return Immutable({ ...state, messages: sortBy(messages, 'createDate') });
        }
        case EDIT_ROOM_MESSAGES: {
            // A parent message edited/removed in the room — refresh BOTH the open thread's
            // pinned parent AND its threads-list card in real time (e.g. delete → "Message
            // removed"), preserving the thread meta (tcount/replies/tlm).
            const { message } = payload || {};
            if (error || !message) {
                return state;
            }
            const merge = m => (m && m.id === message.id
                ? {
                    ...m,
                    ...message,
                    tcount: message.tcount != null ? message.tcount : m.tcount,
                    replies: message.replies && message.replies.length ? message.replies : m.replies,
                    tlm: message.tlm || m.tlm,
                }
                : m);
            return Immutable({
                ...state,
                threadsList: state.threadsList.map(merge),
                parentMessage: merge(state.parentMessage),
            });
        }
        case UPDATE_CHAT_MESSAGE: {
            // Own optimistic parent edit ({ _id, rid, msg }) — the WS EDIT_ROOM_MESSAGES echo
            // is gated out once the room text already matches, so patch the pinned text here.
            if (error || !payload || !state.parentMessage || payload._id !== state.parentMessage.id) {
                return state;
            }
            return Immutable({ ...state, parentMessage: { ...state.parentMessage, text: payload.msg } });
        }
        case DELETE_CHAT_MESSAGE: {
            const { _id } = payload || {};
            if (!_id) {
                return state;
            }
            return Immutable({
                ...state,
                messages: state.messages.filter(m => m.id !== _id),
                threadsList: state.threadsList.filter(t => t.id !== _id),
            });
        }
        default:
            return state;
    }
};
